import { Graphics } from 'pixi.js';
import { Timeline } from '../../../vendor_core/core/src/Timeline';
import { Tween } from '../../../vendor_core/core/src/Tween';
import SpinModel from '../../../vendor_core/core/src/model/SpinModel';
import { State } from '../../../vendor_core/core/src/states/State';
import { DefaultLayers } from '../../../vendor_core/core/src/utils/Layers';
import ViewManager from '../../../vendor_core/core/src/utils/ViewManager';
import ReelGroup from '../../../vendor_core/core/src/views/ReelGroup';
import CoinModel from '../../model/CoinModel';
import ICoin from '../../model/ICoin';
import BitmapTextField from '../../../vendor_core/core/src/views/components/BitmapTextField';
import TextField from '../../../vendor_core/core/src/views/components/TextField';
import BangUp from '../../../vendor_core/core/src/utils/BangUp';
import Utils from '../Utils';
import * as CoreUtils from '../../../vendor_core/core/src/utils/Utils';
import SoundController from '../../../vendor_core/core/src/utils/SoundController';
import { cloneDeep } from 'lodash';
import Layout from '../../../vendor_core/core/src/utils/Layout';


export default class TRWCollectCoinState extends State {
    protected startDelay = 0; // 50
    protected exitDelay = 400; // 500
    protected baseCoinMoveTime = 300; // 800;
    protected baseCoinValueMoveTime = 200; // 400;
    protected coinBounceTime = 100; // 300;
    protected splitCoinAndValuesDuration = 350; // 500;
    protected benGupDuration = 200; // 300;
    protected delayBetweenMoves = 150; // 150;
    protected delayToStayOnScreen = 300; // 600;
    protected multipliersMoveIndex = 2.5;
    protected hasSplitValue = false;
    protected hasOnlyOneMultiplier = false;
    protected benGups: BangUp[] = [];
    protected moveTimeline: Timeline;
    protected exitStateTween: Tween;
    public enter(): void {
        super.enter();
        Utils.prepareTWScene();
        this.setOrientationFn();
        // // Here must play first chest anim !!
        // CoinModel.playCoinRainChestAnim();
        Tween.delayedCall((this.startDelay / 1000), () => this.init())
        // this.init();
    }

    init(): void {
        const collectData = this.getCollectData();
        const tumbleScene = ViewManager.getLayer("ThumbleWinCounter").getCurrentScene();
        const twPlate = tumbleScene.groups.get("tw_plate");

        const multiData = [...collectData.multipliers, ...collectData.rainData.reduce((acc, v) => {
            if (v.multiplier) {
                acc.push(v)
            }

            return acc
        }, [])];
        // When coins is payed
        if (CoinModel.activeCoins >= 5) {

            twPlate.visible = true;
            const valuesData = [...collectData.values, ...collectData.rainData.reduce((acc, v) => {
                if (v.value) {
                    acc.push(v)
                }
                return acc
            }, [])];

            this.collectCoinValuesAndMulti([...this.sortCollectOrder(valuesData), ...this.sortCollectOrder(multiData)]);

        } else {
            twPlate.visible = false;
            const coinData = [...collectData.values, ...collectData.multipliers];
            this.collectCoins(coinData);
            if (!SpinModel.isInFreespins()) {
                SoundController.playSound("coinAddToChest");
                SoundController.playSound("coinFlyUp", false);
                if (!CoinModel.hasCoinRain) {
                    Tween.delayedCall((this.baseCoinMoveTime / 1000), () => {
                        CoinModel.playChestCollectAnim();
                    });
                }
            }
        }

        this.setExitState(this.getExitDelay(CoinModel.activeCoins, multiData.length))
    }

    getExitDelay(coinsNumber: number, multiplierNumL: number): number {
        let delayInMs = 0;

        if (CoinModel.activeCoins >= 5) {
            delayInMs = this.startDelay
            + (coinsNumber * (this.baseCoinValueMoveTime + this.coinBounceTime + this.delayBetweenMoves))
            + (this.hasSplitValue ? (2 * this.splitCoinAndValuesDuration) + this.exitDelay : this.exitDelay)
            + (multiplierNumL * this.baseCoinValueMoveTime * this.multipliersMoveIndex * 0.5);
        } else {
            if (SpinModel.isInFreespins()) {
                delayInMs = this.startDelay;
            } else {
                if (CoinModel.hasCoinRain) {
                    delayInMs = this.startDelay + (this.baseCoinMoveTime / 3);
                } else {
                    delayInMs = this.startDelay + (this.baseCoinMoveTime) + 350;
                }
            }
        }

        return delayInMs / 1000
    }

    setExitState(delay: number): void {
        this.exitStateTween = Tween.delayedCall(delay, () => {
            this.exit()
        })

    }

    getCollectData = (): { values: ICoin[], multipliers: ICoin[], rainData: ICoin[] } => {
        const data: ICoin[] = CoreUtils.default.deepClone(SpinModel.generations[SpinModel.cascadeIndex].currentCoins);
        const coinRainData: ICoin[] = CoreUtils.default.deepClone(SpinModel.generations[SpinModel.cascadeIndex].rainCoins);
        const result = data.sort((a, b) => a.coordinate.x - b.coordinate.x).reduce((acc, v) => {
            if (v.value) {
                acc.values.push(v);
            } else {
                acc.multipliers.push(v);
            }

            return acc
        }, {
            values: [],
            multipliers: [],
            rainData: []
        })

        coinRainData ? result.rainData = coinRainData.sort((a, b) => a.coordinate.x - b.coordinate.x) : null;

        return result
    };

    /**
     * Move not payable coins to the chest
     * @param data all coins coordinates
     */
    public collectCoins = (data: ICoin[]) => {
        const moveScene = ViewManager.getLayer("Move").getCurrentScene();
        CoinModel.stopAnticipation();
        const coinBarScene = ViewManager.getLayer("CoinBar")?.getCurrentScene();
        const coinAnim = coinBarScene.spineAnimations.get("collection_5");
        coinAnim.play("blank");
        CoinModel.setCoinCollectionState(5, true);
        if (!SpinModel.isInFreespins()) {
            data.forEach((d, i) => {
                const anim = moveScene.spineAnimations.get(`collect_${i+1}`);
                const idx: number = d.coordinate.x * 5 + d.coordinate.y + 1; // reel_0_4 -> idx = 4
                anim.play(`cell_${idx}`, false, 1.5);
            })
        }
    };
    /**
     * Collect all values and multipliers to tumble win plate
     */
    public collectCoinValuesAndMulti = (data: ICoin[]) => {
        const scene = ViewManager.getLayer(
            DefaultLayers.Reels
        ).getCurrentScene();
        this.hasOnlyOneMultiplier = false;
        const multipliersNumber = data.reduce((acc, v) => {
            if (v.multiplier > 0) {
                acc += 1;
            }
            return acc
        }, 0);
        if (multipliersNumber === 1) {
            this.hasOnlyOneMultiplier = true;
        }


        const tumbleScene = ViewManager.getLayer("ThumbleWinCounter").getCurrentScene();
        // const twPlate = tumbleScene.groups.get("tw_plate");
        // const winTF = tumbleScene.sceneMoneyFields.get("tw_counter");
        // const totalWin = tumbleScene.sceneMoneyFields.get("tw_total");
        const multiplierTF = tumbleScene.sceneTextFields.get("multi");
        multiplierTF.text = "";

        const reelGroup = scene.getSubView("ReelGroup") as ReelGroup;
        // reelGroup.sortSymbols();

        const moveScene = ViewManager.getLayer("Move").getCurrentScene();
        const group = moveScene.groups.get("reels");
        const targetMulti = moveScene.shapes.get("target_multi");
        const targetValue = moveScene.shapes.get("target");

        this.moveTargetBounds(false);
        this.zoomPlate(false);

        this.hasSplitValue = false;
        let delay = 0;

        this.moveTimeline = new Timeline();

        data.forEach((c, i) => {
            const sym = reelGroup.getSymbolAt(c.coordinate.x, c.coordinate.y);
            const symPosition = sym.getGlobalPosition();
            // const fontSize = (c.value ? overlayConfig["M3"] : overlayConfig["M4"])["fontSize"];
            const target = c.value ? targetValue : targetMulti;
            const item = CoinModel.getSymbolTextOverlay(sym);
            group.addChild(item);

            const convertedCoordinates = item.parent.toLocal(symPosition);
            const offsetY: number = 72 * Layout.globalScale;
            // item.position.set(convertedCoordinates.x, convertedCoordinates.y);
            // if (c.value) {
                // item.position.set((convertedCoordinates.x - (item["_text"]["width"] / 2)), convertedCoordinates.y - (fontSize / 2 * Layout.globalScale));
                item.position.set((convertedCoordinates.x - (item["_text"]["width"] / 2)), convertedCoordinates.y - (item["_text"]["height"] / 2) + offsetY);
            //     // item.position.set((convertedCoordinates.x - (item["_text"]["width"] / 2)), convertedCoordinates.y - (fontSize / 2 * Layout.globalScale));
            // } else {
            //     item.position.set((convertedCoordinates.x - (item["_text"]["width"] / 2)), convertedCoordinates.y - (fontSize / 2 * Layout.globalScale));
            //     // item.position.set((convertedCoordinates.x - (item["_text"]["width"] / 2)), convertedCoordinates.y + (fontSize / 2));
            // }
            // dirty hack, because the text overlay is scaling up on mobile
            if (!Layout.isLandscape()) {
                item.scale.set(0.79);
                item.position.x += c.value ? 16 * Layout.globalScale : 12 * Layout.globalScale;
                item.position.y += -6 * Layout.globalScale;
            }
            sym.cleanTextOverlay();
            if (c.multiplier && !this.hasSplitValue) {
                this.splitValuesAndMulti(this.moveTimeline, i, delay)
                this.hasSplitValue = true;
                delay += (this.splitCoinAndValuesDuration / 1000);
            };

            this.addBounceEffect(
                this.moveTimeline,
                item,
                i,
                delay,
                c?.multiplier > 0
            );
            delay += (this.coinBounceTime / 1000);
            this.moveValues
                (
                    this.moveTimeline,
                    item,
                    target,
                    i,
                    delay,
                    c?.multiplier > 0,
                    () => {
                        c.multiplier ? multiplierTF.alpha = 1 : null
                        this.benGups.push(CoinModel.updateTmWin(c.value, c.multiplier, (this.benGupDuration / 1000)));

                        const offsetX: number = Layout.isLandscape() ? 30 : 10;
                        const offsetY: number = Layout.isLandscape() ? 0 : -15;

                        multiplierTF.scale.set(1.6);
                        multiplierTF.position.x += offsetX * Layout.globalScale;
                        multiplierTF.position.y += offsetY * Layout.globalScale;
                    },
                );

            this.moveTimeline.add(() => {
                sym.playAnimation("skull")
            }, (delay + (this.baseCoinValueMoveTime / 1000 * i)));

            delay += (this.delayBetweenMoves / 1000);
        })

        if (this.hasSplitValue) {
            this.mergeValuesAndMulti(this.moveTimeline, data);
        }
        
        this.moveTargetBounds(true);
    }
    /**
     * This is move value/multiplier tween
     * @param item Text Overlay
     * @param target Tumble win plate
     */
    private moveValues = (tl: Timeline, item: BitmapTextField | TextField, target: Graphics, index: number, delay: number, isMultiplier: boolean, onComplete?: Function) => {

        const moveTime = isMultiplier ? (this.baseCoinValueMoveTime / 1000 * this.multipliersMoveIndex) : (this.baseCoinValueMoveTime / 1000);
        const position = (this.hasOnlyOneMultiplier && isMultiplier) ? (delay + (this.baseCoinValueMoveTime / 1000 * index) + (this.delayToStayOnScreen / 3 / 1000)) : (delay + (this.baseCoinValueMoveTime / 1000 * index))
        const offsetY: number = 80 * Layout.globalScale;

        return tl.to(item.position, moveTime, {
            x: target.x,
            y: target.y + offsetY,
            onComplete: () => {
                onComplete && onComplete();
                item.destroy();
            },

        // }, (delay + (this.baseCoinValueMoveTime / 1000 * index)))
        }, position)
    }
    /**
     * This is add bounce value/multiplier tween
     * @param item Text Overlay
     * @param target Tumble win plate
     */
    private addBounceEffect = (tl: Timeline, item: BitmapTextField | TextField, index: number, delay: number, isMultiplier: boolean) => {
        const initScaleX = item.scale.x;
        const initScaleY = item.scale.y;
        const initX = item.x;
        const initY = item.y;

        if (isMultiplier) {
            if (!this.hasOnlyOneMultiplier) {
                tl.addCallback(() => {
                    SoundController.playSound("multiplierTumbleFlyUp", false);
                })
            }
            tl.to(item.scale, (this.coinBounceTime / 1000 / 2), {
                x: item.scale.x * 2,
                y: item.scale.y * 2,
            }, (delay + (this.baseCoinValueMoveTime / 1000 * index)));

            tl.to(item.position, (this.coinBounceTime / 1000 / 2), {
                x: item.x - (item["_text"]["width"] / 2),
                y: item.y - (item["_text"]["height"] / 2),
            }, (delay + (this.baseCoinValueMoveTime / 1000 * index)));
            // tl.addCallback(() => {
            // }, (delay + (this.baseCoinValueMoveTime / 1000 * index)));
            if (this.hasOnlyOneMultiplier) {
                tl.to(item.position, (this.coinBounceTime / 1000 / 2), {
                    x: initX,
                    y: initY,
                }, (delay + (this.baseCoinValueMoveTime / 1000 * index) + (this.coinBounceTime / 1000 / 2) + (this.delayToStayOnScreen / 3 / 1000)));
                tl.addCallback(() => {
                    SoundController.playSound("multiplierTumbleFlyUp", false);
                })
                tl.to(item.scale, (this.coinBounceTime / 1000 / 2), {
                    x: initScaleX,
                    y: initScaleY,
                }, (delay + (this.baseCoinValueMoveTime / 1000 * index) + (this.coinBounceTime / 1000 / 2) + (this.delayToStayOnScreen / 3 / 1000)));

            } else {
                tl.to(item.position, (this.coinBounceTime / 1000 / 2), {
                    x: initX,
                    y: initY,
                }, (delay + (this.baseCoinValueMoveTime / 1000 * index) + (this.coinBounceTime / 1000 / 2)));

                tl.to(item.scale, (this.coinBounceTime / 1000 / 2), {
                    x: initScaleX,
                    y: initScaleY,
                }, (delay + (this.baseCoinValueMoveTime / 1000 * index) + (this.coinBounceTime / 1000 / 2)));
            }
        } else {
            tl.addCallback(() => {
                SoundController.playSound("coinAddTumble", false);
            })
            tl.to(item.scale, (this.coinBounceTime / 1000 / 2), {
                x: item.scale.x * 2,
                y: item.scale.y * 2,
            }, (delay + (this.baseCoinValueMoveTime / 1000 * index)));

            tl.to(item.position, (this.coinBounceTime / 1000 / 2), {
                x: item.x - (item["_text"]["width"] / 2),
                y: item.y - (item["_text"]["height"] / 2),
            }, (delay + (this.baseCoinValueMoveTime / 1000 * index)));
            // tl.addCallback(() => {
            // }, (delay + (this.baseCoinValueMoveTime / 1000 * index)));
            tl.to(item.position, (this.coinBounceTime / 1000 / 2), {
                x: initX,
                y: initY,
            }, (delay + (this.baseCoinValueMoveTime / 1000 * index) + (this.coinBounceTime / 1000 / 2)));

            tl.to(item.scale, (this.coinBounceTime / 1000 / 2), {
                x: initScaleX,
                y: initScaleY,
            }, (delay + (this.baseCoinValueMoveTime / 1000 * index) + (this.coinBounceTime / 1000 / 2)));
        }


        return tl
    }
    /**
     * This is move value/multiplier tween
     * @param item Text Overlay
     * @param target Tumble win plate
     */
    private splitValuesAndMulti = (tl: Timeline, index: number, delay: number) => {
        const scene = ViewManager.getLayer("ThumbleWinCounter").getCurrentScene();
        const value = scene.sceneMoneyFields.get("tw_counter");
        const totalWin = scene.sceneMoneyFields.get("tw_total");
        // const multi = scene.sceneTextFields.get("multi");
        const initPosition = totalWin.position.clone();

        return tl.to(totalWin.position, (this.baseCoinValueMoveTime / 1000), {
            // x: value.x,
            // y: value.y,
            x: "-=" + 60 * Layout.globalScale,
            /* onComplete: () => {
                value.alpha = 1;
                totalWin.alpha = 0;
                totalWin.position.set(initPosition.x, initPosition.y);
            }, */
            onStart: () => {
                value.value = SpinModel.cascades[SpinModel.cascadeIndex].tempWinAmount;
            }
            // onComplete: () => item.playBounceEffect(1, Layout.globalScale, Layout.globalScale * 1.5, () => {
            // }),
            // onStart: () => item.playBounceEffect(1, Layout.globalScale * 1.5, Layout.globalScale)
        }, (delay + (this.baseCoinValueMoveTime / 1000 * index)))
    }
    /**
     * This is move value/multiplier tween
     * @param item Text Overlay
     * @param target Tumble win plate
     */
    private mergeValuesAndMulti = (tl: Timeline, data: ICoin[]) => {

        const scene = ViewManager.getLayer("ThumbleWinCounter").getCurrentScene();

        const value = scene.sceneMoneyFields.get("tw_counter");
        const valueInitPosition = value.position.clone();

        const totalWin = scene.sceneMoneyFields.get("tw_total");
        const multi = scene.sceneTextFields.get("multi");

        const multiInitPosition = multi.position.clone();

        const positionTimeLine = (((
            (this.baseCoinValueMoveTime + this.coinBounceTime + this.delayBetweenMoves) * data.length)
            // Here add split time
            + this.splitCoinAndValuesDuration)
            // Here add more time for different multipliers move time
            + (data.filter(d => d.multiplier > 0).length * this.baseCoinValueMoveTime * this.multipliersMoveIndex * 0.5)
            // add delay to stay multiplier in to the screen for a bit
            + (this.hasOnlyOneMultiplier ? this.delayToStayOnScreen : 0))
            // Here convert to seconds
            / 1000;

        // const initScale = totalWin.scale.clone();

        // old animation
        /* tl.to(value.position, (this.baseCoinValueMoveTime / 1000), {
            x: totalWin.x,
            y: totalWin.y,
            onComplete: () => {
                value.alpha = 0;
                value.position.set(valueInitPosition.x, valueInitPosition.y)
            },
            // onComplete: () => item.playBounceEffect(1, Layout.globalScale, Layout.globalScale * 1.5, () => {
            // }),
            // onStart: () => value.playBounceEffect(1, Layout.globalScale * 1.5, Layout.globalScale)
        }, positionTimeLine); */

        const offsetMultiX: number = 40 * Layout.globalScale;
        const offsetTotalWinX: number = Layout.isLandscape() ? 60 * Layout.globalScale : 50 * Layout.globalScale;

        // new animation
        tl.to(totalWin.position, (this.baseCoinValueMoveTime / 1000), {
            x: "+=" + offsetTotalWinX
        }, positionTimeLine);

        return tl.to(multi.position, (this.baseCoinValueMoveTime / 1000), {
            x: totalWin.x - offsetMultiX /* + totalWin.width / 2 */,
            onComplete: () => {
                multi.alpha = 0;
                multi.position.set(multiInitPosition.x, multiInitPosition.y)
                this.benGups.push(CoinModel.updateTmWin(0, 0, (this.benGupDuration / 1000)));
                this.zoomPlate(true);
                SoundController.playSound("tumbleWinMerge");
            }
            // onComplete: () => item.playBounceEffect(1, Layout.globalScale, Layout.globalScale * 1.5, () => {
            // }),
            // onStart: () => item.playBounceEffect(1, Layout.globalScale * 1.5, Layout.globalScale)
        }, positionTimeLine)
    }

    public canExit(): boolean {
        /**
         * Override method to alow externel skip of state
         */
        return true;
    }

    public forceFinishStateTweens = () => {
        if (CoinModel.hasCoinRain) {

        } else {
            if (this.moveTimeline?.isActive()) {
                this.exitStateTween.kill();
                this.moveTimeline.timeScale = 1000;
                // this.moveTimeline.kill();
                this.moveTimeline = null;
            }
        }

    }

    public setOrientationFn = () => {
        const twScene = ViewManager.getLayer("ThumbleWinCounter").getCurrentScene();
        twScene.orientationChangeCallback = () => {
            if (this.moveTimeline?.isActive()) {
                this.exitStateTween.kill();
                this.moveTimeline.kill();
                this.moveTimeline = null;
                this.benGups.forEach(b => b?.clearBangup());
                const totalWin = twScene.sceneMoneyFields.get("tw_total");
                totalWin.value = SpinModel.accumulatedWin;
                ReelGroup.instance.reels.forEach(r => r.symbols.forEach(s => s.id === 10 ? s.playAnimation("skull") : null))
                this.exit();
            }
        };
    }

    sortCollectOrder(data: ICoin[]): ICoin[] {
        return cloneDeep(data).sort((a, b) => {
            let result = a.coordinate.x - b.coordinate.x;
            if (result === 0) {
                result = a.coordinate.y - b.coordinate.y;
            }
            return result
        });;

    }

    private zoomPlate(onExit: boolean): void {
        const duration: number = onExit ? 0.4 : 1.5;
        const totalWinLandscapeScale: number = onExit ? 1.8 : 1.75;
        const totalWinPortraitScale: number = onExit ? 1.5 : 1.4;
        const offsetTotalPlateX: number = onExit ? 0 : Layout.isLandscape() ? -70 : -50;
        const offsetTotalPlateY: number = onExit ? 0 : Layout.isLandscape() ? -4 : 4;
        const offsetPlateX: number = onExit ? -20 : -150;
        const plateScale: number = onExit ? 1.5 : 1.45;

        const tumbleScene = ViewManager.getLayer("ThumbleWinCounter").getCurrentScene();
        const twPlateSprite = tumbleScene.sceneSprites.get("multiplierCounter_panel");
        const totalWin = tumbleScene.sceneMoneyFields.get("tw_total");

        const totalWinScale: number = Layout.isLandscape() ? totalWinLandscapeScale : totalWinPortraitScale;

        Tween.to(totalWin.scale, duration, {
            x: totalWinScale * Layout.globalScale,
            y: totalWinScale * Layout.globalScale
        });

        Tween.to(totalWin.position, duration, {
            x: "+=" + offsetTotalPlateX * Layout.globalScale,
            y: "+=" + offsetTotalPlateY * Layout.globalScale
        });

        Tween.to(twPlateSprite.scale, duration, {
            x: plateScale * Layout.globalScale,
            y: plateScale * Layout.globalScale
        });

        Tween.to(twPlateSprite.position, duration, {
            x: "+=" + offsetPlateX * Layout.globalScale
        });
    }

    private moveTargetBounds(onExit: boolean): void {
        const moveScene = ViewManager.getLayer("Move").getCurrentScene();
        const targetMulti = moveScene.shapes.get("target_multi");
        const targetValue = moveScene.shapes.get("target");

        const multiply: number = onExit ? -1 : 1;

        const targetOffsetY: number = Layout.isLandscape() ? 15 * Layout.globalScale : 25 * Layout.globalScale;

        targetMulti.y += targetOffsetY * multiply;
        targetMulti.x += 60 * Layout.globalScale * multiply;

        targetValue.y += targetOffsetY * multiply;
    }

    public exit(): void {
        this.forceFinishStateTweens();
        Tween.delayedCall(0.5, () => super.exit());
    }
}
